<template>
  <table-wrapper>
    <v-data-table-server
      v-model="selectedItems"
      class="coronet-table--highlightable"
      :items="items"
      :loading="loading"
      fixed-header
      :items-length="totalItems"
      show-select
      item-key="id"
      :items-per-page="pagination.pageSize"
      :no-data-text="$t('general.noDataAvailable')"
      return-object
      @update:options="
        $emit('page-changed', {
          pageSize: $event.itemsPerPage,
          page: $event.page - 1,
        })
      "
    >
      <template #headers>
        <tr>
          <th class="checkbox-col">
            <bulk-selector-checkbox
              class="ml-2"
              :is-indeterminate="isIndeterminate"
              :all-items-on-page-selected="allItemsOnPageSelected"
              :items="items"
              :all-items-selected="allItemsSelected"
              @selection-changed="onSelectionChanged"
            />
          </th>
          <template v-if="selectedItems.length === 0">
            <th>
              {{ $t(`network.settings.trustedNetworksTable.headers.name`) }}
            </th>
            <th>
              {{ $t(`network.settings.trustedNetworksTable.headers.dateAdded`) }}
            </th>
            <th />
          </template>
          <template v-else>
            <th>
              <span>{{ selectedItemsQuantity }} {{ $t("general.selected") }}</span>
              <v-menu v-if="!!selectedItems.length" offset-y bottom right>
                <template #activator="{ props, isActive }">
                  <v-btn
                    data-testid="bulk-actions-button"
                    class="ml-8"
                    rounded
                    color="primary"
                    v-bind="props"
                  >
                    {{ $t("general.actions") }}
                    <v-icon
                      class="ml-1"
                      size="10"
                      icon="$triangle"
                      :class="{ 'rotate-180': isActive }"
                    />
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="action in bulkActions"
                    :key="action"
                    @click="onBulkActionClick(action)"
                  >
                    <v-list-item-title>
                      {{ $t(`network.settings.trustedNetworksTable.actions.${action}`) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </th>
            <th>
              {{ $t(`network.settings.trustedNetworksTable.headers.dateAdded`) }}
            </th>
            <th />
          </template>
        </tr>
      </template>
      <template #item="{ item, index, internalItem, isSelected }">
        <tr
          :key="index"
          :data-testid="`table-row-${index}`"
          :class="{ 'selected-row': isSelected([internalItem]) }"
        >
          <td class="checkbox-col">
            <v-checkbox-btn
              :data-testid="`table-checkbox-row-${index}`"
              :model-value="isSelected([internalItem])"
              @update:modelValue="handleOneRowSelection($event, item)"
              @click.stop
            />
          </td>
          <td
            :data-testid="`trusted-networks-table-name-col-row-${index}`"
            class="d-flex align-center"
          >
            <v-icon size="40" class="mr-4" :icon="getIcon(item.type)" color="primary" />
            <ellipsified-copy-text width="550px" :text="item.value" hide-copy-btn />
          </td>
          <td :data-testid="`trusted-networks-table-date-col-row-${index}`">
            <span class="body2">{{ getFormattedDateTime(item.dateAdded) }}</span>
          </td>
          <td class="text-right coro-table-col--sticky-right">
            <v-menu bottom left>
              <template v-slot:activator="{ props }">
                <v-icon icon="$dots" class="item-clickable" v-bind="props" />
              </template>
              <v-list>
                <v-list-item
                  v-for="action in itemActions"
                  :key="action"
                  @click="onActionClick(action, item)"
                >
                  <v-list-item-title :data-testid="`trusted-networks-table-${action}-action`">
                    {{ $t(`network.settings.trustedNetworksTable.actions.${action}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType, toRefs } from "vue";
import type { TrustedNetworkItem } from "@/_store/network/network-module-settings.module.ts";
import type { Pagination } from "@/types.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import { useSelectableTable } from "@/composables/useSelectableTable.ts";
import { getFormattedDateTime } from "@/_helpers/date";
import { storeToRefs } from "pinia";
import { useSelectorStore } from "@/_store/selector.module.ts";
import BulkSelectorCheckbox from "@/components/BulkSelectorCheckbox.vue";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { TrustedNetworkAction, TrustedNetworkType } from "@/constants/network.ts";

export default defineComponent({
  components: { TableWrapper, BulkSelectorCheckbox, EllipsifiedCopyText },
  props: {
    items: {
      type: Array as PropType<TrustedNetworkItem[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => {
        return {
          page: 0,
          pageSize: 25,
        };
      },
    },
  },
  emits: ["action-applied", "page-changed"],
  setup(props, { emit }) {
    const { totalItems, items } = toRefs(props);
    const {
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsSelected,
      allItemsOnPageSelected,
      selectedItemsQuantity,
    } = useSelectableTable({
      totalItems,
      items,
    });

    const { selection } = storeToRefs(useSelectorStore());

    const onPageChange = (payload: Pagination) => {
      emit("page-changed", payload);
    };

    const onBulkActionClick = (action: TrustedNetworkAction) => {
      emit("action-applied", {
        action,
        selection: selection.value,
        quantity: selectedItemsQuantity.value,
      });
    };

    const onActionClick = (action: TrustedNetworkAction, item: TrustedNetworkItem) => {
      emit("action-applied", {
        action,
        selection: { isAllPages: false, include: [item], exclude: [] },
        quantity: 1,
      });
    };

    const getIcon = (type: TrustedNetworkType) => {
      if (type === TrustedNetworkType.SSID) {
        return "$wifiTrusted";
      }
      return "$globeLocation";
    };

    return {
      getIcon,
      onPageChange,
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsSelected,
      selectedItemsQuantity,
      getFormattedDateTime,
      selection,
      onBulkActionClick,
      onActionClick,
      allItemsOnPageSelected,
      bulkActions: [TrustedNetworkAction.DELETE],
      itemActions: [TrustedNetworkAction.EDIT, TrustedNetworkAction.DELETE],
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

.selected-row {
  background-color: rgb(var(--v-theme-indigo-pale)) !important;
}

:deep(.rotate-180) {
  transform: rotate(180deg);
}
</style>
